import { SDKCollection } from './collection.js';
import { assignDeep, isDeepEquals } from '../utils/object.js';
export class SDKModel {
    get adapter() {
        throw new Error('Adapter not defined');
    }
    /**
     * Create instance of a model. If no data is provided, it's just empty instance. Otherwise data needs to contain all
     * the required fields, and the model will be considered "new". Usually it is a better idea to instantiate models via
     * collections, as it provides necessary implicit attributes.
     */
    constructor(data) {
        this.defineProperties();
        if (data) {
            this.new(data);
        }
    }
    new(data) {
        if (data != null) {
            this.setDefaults().set(data);
            this.isNew = true;
        }
        return this;
    }
    /**
     * To ensure that models look as plain objects all extra properties and method needs to be hidden through the use of
     * `enumerable: false` descriptors.
     */
    defineProperties() {
        Object.defineProperty(this, 'observers', {
            enumerable: false,
            value: []
        });
        Object.defineProperty(this, 'snapshotted', {
            enumerable: false,
            writable: true
        });
        this.sdk.utils.defineAccessor(this, 'isNew', { enumerable: false }, this.getHiddenProps());
        this.getHiddenProps().map((prop) => {
            Object.defineProperty(this, prop, {
                enumerable: false,
                writable: true,
                configurable: true
            });
        }, this);
        this.onConstruct();
    }
    /** Constructor hook for subclasses */
    onConstruct() { }
    getHiddenProps() {
        return [];
    }
    getDefaults(sdk) {
        return {};
    }
    setDefaults() {
        const values = this.getDefaults(this.sdk);
        for (var property in values) {
            if (this[property] === undefined && values[property] !== undefined) {
                this[property] = values[property];
            }
        }
        return this;
    }
    /** Assign values from given object */
    set(data) {
        var changed = false;
        var property;
        for (property in data) {
            /**
             * @ts-ignore fix  me*
             */
            if (this[property] !== data[property]) {
                changed = true;
            }
        }
        Object.assign(this, data);
        if (changed) {
            this.onChange();
        }
        return this;
    }
    merge(data) {
        return assignDeep(this, data);
    }
    copyHiddenProps(data, overwrite) {
        this.getHiddenProps().map((prop) => {
            const current = this[prop];
            if (overwrite ||
                typeof current == 'undefined' ||
                (current && current instanceof SDKCollection) ||
                (current && current instanceof SDKModel))
                this[prop] = data[prop];
        });
        if (data.snapshotted)
            this.snapshotted = data.snapshotted;
        this.isNew = data.isNew;
        return this;
    }
    /** Assign values and hidden properties from given object */
    assign(data) {
        this.set(data);
        if (data && data instanceof this.constructor) {
            this.copyHiddenProps(data, true);
        }
        return this;
    }
    /** Set previously saved valeus to the model, returns clone */
    populate(data) {
        const changed = this.set(data);
        changed.isNew = false;
        return changed;
    }
    /** Return clone if any of given values are different */
    change(data) {
        var property;
        for (property in data) {
            const left = this[property];
            const right = data[property];
            if (!this.compareProp(property, left, right)) {
                return this.clone(data);
                break;
            }
        }
        return this;
    }
    put(data) {
        this.set(data);
        return this.adapter.put(this).then((data) => this.populate(data));
    }
    /* Upsert model based on its isNew flag - either update or create it */
    save(data) {
        this.set(data);
        this.setDefaults();
        if (this.isNew) {
            return this.post();
        }
        else {
            return this.put();
        }
    }
    update(data) {
        return this.put(data);
    }
    post(data) {
        this.set(data);
        return this.adapter.post(this).then((data) => this.populate(data));
    }
    insert(data) {
        return this.post(data);
    }
    delete() {
        if (!this.isNew) {
            return this.adapter.delete(this).then((data) => this.populate(data));
        }
        return Promise.resolve(this);
    }
    get() {
        return this.adapter.get(this).then((data) => this.populate(data));
    }
    fetch(options) {
        return this.adapter.fetch(this.clone(options)).then((collection) => {
            return collection.map((data) => this.construct(data));
        });
    }
    select(options) {
        return this.fetch(options);
    }
    search(query) {
        return this.adapter
            .search(Object.assign(this.export(), query))
            .then((collection) => collection.map((data) => this.construct(data)));
    }
    /** Create new instance of this class */
    construct(data) {
        const instance = Object.create(Object.getPrototypeOf(this));
        instance.defineProperties();
        return instance.set(data);
    }
    /** Clone instance retaining data & hidden properties */
    clone(data) {
        const clone = this.construct();
        Object.assign(clone, this);
        if (data) {
            clone.set(data);
        }
        clone.copyHiddenProps(this);
        return clone;
    }
    /** Returns plain object without any descriptors or prototype */
    export() {
        const object = {};
        for (var property in this) {
            if (this.hasOwnProperty(property))
                object[property] = this[property];
        }
        return object;
    }
    serialize() {
        return this.export();
    }
    snapshot() {
        this.snapshotted = this.export();
        return this;
    }
    /** Default sorting logic */
    sortCompare(other) {
        return 0;
    }
    getId() {
        return this.id;
    }
    /** Returns object that has fresh identity but references same collection */
    /** Useful for shallow equality checked deps */
    proxy() {
        return new Proxy(this, {
            get(target, prop) {
                if (prop == Symbol.toStringTag) {
                    return target[Symbol.toStringTag] + 'Proxy';
                }
                return target[prop];
            }
        });
    }
    onChange() {
        var _a, _b;
        if ((_a = this.observers) === null || _a === void 0 ? void 0 : _a.length)
            (_b = this.observers) === null || _b === void 0 ? void 0 : _b.forEach((observer) => observer.call(this, this), this);
    }
    observe(observer) {
        ;
        (this.observers || (this.observers = [])).push(observer);
    }
    unobserve(observer) {
        const index = this.observers.indexOf(observer);
        if (index > -1)
            this.observers.splice(index, 1);
    }
    get isNew() {
        return this._isNew || false;
    }
    set isNew(value) {
        this._isNew = value;
    }
    /** Compares changes */
    isEqual(other, ignoreProps = ['modifiedAt', 'createdAt', 'sampledAt']) {
        return isDeepEquals(this, other, ignoreProps);
    }
    compareProp(property, value1, value2) {
        return value1 && value1 instanceof Date && value2 && value2 instanceof Date
            ? Number(value1) == Number(value2)
            : value1 == value2 || false;
    }
    static onCollectionChange(collection) {
        return collection.sort((a, b) => a.sortCompare(b));
    }
    /** Apply normalization on style */
    static transformAttributes(object) {
        return object;
    }
}
SDKModel.prototype.select = SDKModel.prototype.fetch;
//# sourceMappingURL=model.js.map